import { useState } from 'react';
import { api } from '../utils/api';

export const ContactStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  REPLIED: 'REPLIED',
  CLOSED: 'CLOSED',
} as const;

export type ContactStatus = typeof ContactStatus[keyof typeof ContactStatus];

export interface ContactReply {
  fileUrl: any;
  id: string;
  content: string;
  authorId: string;
  author: {
    prenom: string;
    nom: string;
    photoProfil?: string;
  };
  isRead: boolean;
  createdAt: string;
}

export interface ContactMessage {
  id: string;
  prenom: string;
  nom: string;
  email: string;
  telephone?: string;
  entreprise?: string;
  sujet: string;
  message: string;
  traite: boolean;
  status: ContactStatus;
  type?: string;
  notes?: string;
  replies?: ContactReply[];
  createdAt: string;
  updatedAt: string;
  fileUrl?: string;
  isRead: boolean;
}

export interface ContactFormData {
  prenom: string;
  nom: string;
  email: string;
  telephone?: string;
  entreprise?: string;
  sujet: string;
  message: string;
  type?: string;
  fileUrl?: string;
}

export function useContact() {
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const data = await api.get('/contact');
      setMessages(data || []);
      return data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération des messages');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchMyMessages = async () => {
    setLoading(true);
    try {
      const data = await api.get('/contact/my-messages');
      setMessages(data || []);
      return data;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération de vos messages');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const submitContact = async (data: ContactFormData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      await api.post('/contact', data);
      setSuccess(true);
      return { success: true };
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Une erreur est survenue lors de l\'envoi du message.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (messageId: string, content: string, fileUrl?: string, refreshType: 'all' | 'my' = 'all') => {
    setLoading(true);
    try {
      const res = await api.post(`/contact/${messageId}/reply`, { content, fileUrl });
      if (refreshType === 'all') await fetchMessages();
      else await fetchMyMessages();
      return res;
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erreur lors de l\'envoi de la réponse';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: ContactStatus) => {
    setLoading(true);
    try {
      await api.patch(`/contact/${id}/status`, { status });
      await fetchMessages();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setLoading(false);
    }
  };

  const resetStatus = () => {
    setSuccess(false);
    setError(null);
  };

  return {
    messages,
    fetchMessages,
    fetchMyMessages,
    submitContact,
    sendReply,
    updateStatus,
    loading,
    error,
    success,
    resetStatus
  };
}
